import React, { useEffect, useRef, useState } from "react";

import { TabMenu } from "primereact/tabmenu";
import { Outlet, useNavigate } from "react-router-dom";
import { Dropdown } from "primereact/dropdown";
import { useCreateProjectMutation, useGetProjectByRefNameQuery } from "gql/generated/types";
import { useGetCurrentUser } from "api/api-hooks/user-hooks";
import { Button } from "primereact/button";
import { OverlayPanel } from "primereact/overlaypanel";
import InputLabelWrapper from "components/basic/input-label-wrapper/InputLabelWrapper";
import { InputText } from "primereact/inputtext";
import { easasAlert } from "components/fragments/easas-global-fragment/EasasGlobalAlerts";

const Layout = () => {
  const [activeIndex, setActiveIndex] = useState(2);
  const [projectId, setProjectId] = useState<string | null>(null);
  const {data: userData} = useGetCurrentUser();

  useEffect(() => {
    const { hash } = window.location;
    if (hash === "#/" || hash === "") return setActiveIndex(0);
    if (hash === "#/devices") return setActiveIndex(1);
    if (hash === "#/map") return setActiveIndex(2);
    return setActiveIndex(10);
  }, [window.location.hash]);
  
  const navigate = useNavigate();
  const items = [
    {
      label: "Objects",
      icon: "pi pi-list",
      command: () => {
        navigate("/");
      },
    },
    {
      label: "Devices",
      icon: "pi pi-list",
      command: () => {
        navigate("/devices");
      },
    },
    {
      label: "Map",
      icon: "pi pi-map",
      command: () => {
        navigate("/map");
      },
    },
  ];

  return (
    <div>
      <div className="flex gap-1">
        <TabMenu
          model={items}
          className="flex-1 justify-content-end"
          activeIndex={activeIndex}
          onTabChange={e => setActiveIndex(e.index)}
        />
        {userData?.companyRef && (
          <ProjectDropDown
            companyRef={userData.companyRef}
            projectId={projectId}
            setProjectId={setProjectId}
          />
        )}
      </div>
      <Outlet context={projectId} />
    </div>
  );
};

export default Layout;

type ProjectDropDownProps = {
  projectId: string | null;
  setProjectId: (projectId: string) => void;
  companyRef: string
}

const ProjectDropDown: React.FC<ProjectDropDownProps> = ({ projectId, setProjectId, companyRef }) => {
  const [projectName, setProjectName] = useState("");
  const op = useRef<OverlayPanel>(null)
  const { data } = useGetProjectByRefNameQuery({
    variables: { refName: companyRef },
  });

  const [mutateFunction] = useCreateProjectMutation({
    onCompleted: () => {
      easasAlert({
        severity: "success",
        summary: "Success",
        detail: "Project created successfully",
        life: 1000,
      });
    },
    onError: err => {
      easasAlert({ severity: "error", summary: "Error", detail: err.message, life: 3000 });
    },
    refetchQueries: ["GetProjectByRefName"],
  });

  const handleCreateProject = () => {
    if (!projectName) return;
    mutateFunction({
      variables: {
        project: { companyReference: companyRef, title: projectName },
      },
    });
    setProjectName("");
    op.current?.hide();
  }

  return (
    <div className="p-inputgroup w-15rem">
      <Dropdown
        value={projectId}
        options={data?.mdmProjectsByCompany || []}
        optionLabel="title"
        optionValue="id"
        onChange={e => setProjectId(e.value)}
        placeholder="Select project"
        className="w-13rem"
      />
      <Button
        icon="pi pi-plus"
        onClick={e => op.current?.toggle(e)}
        className="p-inputgroup-addon p-button p-button-success p-button-text bg-gray-200 border-gray-300"
      />
      <OverlayPanel ref={op}>
        <InputLabelWrapper labelName="Project name" className="w-full">
          <div className="p-inputgroup">
            <InputText
              value={projectName}
              placeholder="Project name"
              onChange={e => setProjectName(e.target.value)}
            />
            <Button
              label="Create"
              className="p-button-success"
              onClick={handleCreateProject}
            />
          </div>
        </InputLabelWrapper>
      </OverlayPanel>
    </div>
  );
};